import { useEffect, useRef, useState } from "react";
import { classNames } from "../../../helper/classNames";
import { FeedbackField, FeedbackFormDef } from "./forms";
import { FeedbackValues } from "../../../services/feedbackSubmission";

/**
 * The one feedback form renderer. Maps over `form.fields` and switches on
 * `field.type`; owns value state, validation, the error banner and the submit
 * button. Knows nothing about tables, auth, or whether its dialog can be
 * dismissed — those belong to the wrapper.
 *
 * Styling is carried over verbatim from the original hardcoded FeedbackForm so
 * the onboarding gate and the footer form remain visually identical.
 */

type Props = {
  form: FeedbackFormDef;
  /**
   * Both wrappers catch their own failures and swap in the error panel, so this
   * normally resolves either way. It still surfaces a rejection inline as a
   * fallback, for a wrapper that hasn't wired the panel up.
   */
  onSubmit: (values: FeedbackValues) => Promise<void>;
  submitLabel?: string;
  /**
   * Omit to render no cancel button at all — which is how the onboarding gate
   * stays a gate. Its presence is the wrapper's statement that the form is
   * escapable, so there is no way to end up with a dismiss control on a modal
   * that must not be dismissed.
   */
  onCancel?: () => void;
  /**
   * Answers to open with. The retry path needs this: the panel swap unmounts
   * the form to show the error, so coming back would otherwise mean an empty
   * form and every answer retyped. The wrapper holds the draft and hands it
   * back. Read once, at mount — this is an uncontrolled form.
   */
  initialValues?: FeedbackValues;
  /**
   * Called with the current answers as this component unmounts — which happens
   * on the panel swap AND on every dismissal route. The wrapper stores them and
   * hands them back via `initialValues`, so closing the dialog is not a way to
   * lose a half-written answer.
   */
  onDraftChange?: (values: FeedbackValues) => void;
};

const INPUT_CLASS =
  "border-[1.5px] border-gray-400 [.dark_&]:border-zinc-600 [.dark_&]:bg-zinc-800 " +
  "[.dark_&]:text-zinc-100 [.dark_&]:placeholder-zinc-500 rounded-xl w-full py-3 px-4 " +
  "text-md focus-visible:border-[1.5px] focus-visible:border-indigo-400 " +
  "focus-visible:outline-0";

const LABEL_CLASS =
  "block font-medium text-gray-700 [.dark_&]:text-zinc-300 mb-2";

const emptyValues = (form: FeedbackFormDef): FeedbackValues =>
  Object.fromEntries(
    form.fields.map((field) => [field.id, field.type === "rating" ? null : ""]),
  );

/**
 * A rating field is answered when it holds a number — NOT when it is truthy.
 *
 * No shipped form declares a `rating` today, and renderField defaults `min` to
 * 1, so falsiness would happen to work for anything added now. But `min` is
 * per-field config: the moment a form declares `min: 0`, truthiness both
 * rejects a legitimate 0 and lets an untouched `null` field pass as one.
 * Checking the type instead makes this independent of where the scale starts.
 */
const isAnswered = (field: FeedbackField, value: string | number | null) => {
  if (field.type === "rating") {
    return typeof value === "number";
  }
  return typeof value === "string" && value.trim().length > 0;
};

const FeedbackFields: React.FC<Props> = ({
  form,
  onSubmit,
  submitLabel,
  onCancel,
  initialValues,
  onDraftChange,
}) => {
  // Spread over the empty shape rather than used directly: a draft captured
  // before a field was added would otherwise leave that field `undefined`,
  // which is neither "" nor null and reads as answered.
  const [values, setValues] = useState<FeedbackValues>(() => ({
    ...emptyValues(form),
    ...initialValues,
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Read through refs so the effect can stay mount-scoped: a dependency on
  // `values` would re-run it on every keystroke and report a draft on each
  // change instead of once on the way out.
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  useEffect(() => () => onDraftChangeRef.current?.(valuesRef.current), []);

  const setValue = (id: string, value: string | number | null) => {
    setValues((prev) => ({ ...prev, [id]: value }));
    if (error) {
      setError("");
    }
  };

  const missingFields = () =>
    form.fields.filter(
      (field) => field.required && !isAnswered(field, values[field.id]),
    );

  const handleSubmit = async () => {
    // Validated here rather than by disabling the button. A disabled Submit
    // makes an unanswered question indistinguishable from a broken one: the
    // click does nothing and says nothing, and a required question can sit at
    // the bottom of the form, off-screen on short windows. Naming the
    // questions is the whole point of the check.
    const missing = missingFields();
    if (missing.length > 0) {
      setError(
        missing.length === 1
          ? `Please answer: ${missing[0].label}`
          : `Please answer: ${missing.map((field) => field.label).join(" · ")}`,
      );
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await onSubmit(values);
      // Deliberately not clearing isSubmitting on success: the wrapper unmounts
      // or closes us, and re-enabling the button first would let a second click
      // through.
    } catch (err: any) {
      setError(
        err?.message || "An unexpected error occurred. Please try again.",
      );
      setIsSubmitting(false);
    }
  };

  const renderField = (field: FeedbackField) => {
    const value = values[field.id];

    switch (field.type) {
      case "textarea":
        return (
          <textarea
            id={field.id}
            name={field.id}
            // No `rows`/fixed min-height: textareas are the only elastic part of
            // the form, so they flex to consume whatever vertical space is left
            // and shrink on short windows instead of pushing the panel into a
            // scroll. `resize-none` because a manual drag would defeat that.
            className={`${INPUT_CLASS} min-h-[56px] grow shrink basis-[110px] resize-none`}
            placeholder={field.placeholder}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => setValue(field.id, e.target.value)}
            disabled={isSubmitting}
            aria-required={field.required}
            maxLength={field.maxLength}
          />
        );

      case "select":
        return (
          <select
            id={field.id}
            name={field.id}
            className={INPUT_CLASS}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => setValue(field.id, e.target.value)}
            disabled={isSubmitting}
            aria-required={field.required}
          >
            <option value="">{field.placeholder || "Select one…"}</option>
            {field.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case "rating": {
        const min = field.min ?? 1;
        const max = field.max ?? 5;
        const scale = Array.from(
          { length: max - min + 1 },
          (_, index) => min + index,
        );

        return (
          // `self-start` shrinks the group to the width of the buttons, so the
          // endpoint captions sit under the first and last button rather than
          // stretching to the full panel width. It has to be `self-start` and
          // not `inline-block`: the field wrapper is a flex column, which
          // blockifies an inline-block child and then stretches it to full
          // width — which is exactly how "Very likely" ends up at the panel
          // edge instead of under the 5.
          <div
            role="group"
            aria-labelledby={`${field.id}-label`}
            className="self-start"
          >
            <div className="flex flex-wrap gap-2">
              {scale.map((score) => {
                const selected = value === score;
                return (
                  <button
                    key={score}
                    type="button"
                    aria-pressed={selected}
                    disabled={isSubmitting}
                    onClick={() => setValue(field.id, score)}
                    className={classNames(
                      "border-[1.5px] rounded-xl h-12 w-12 text-md font-medium transition-colors",
                      selected
                        ? "bg-royal-blue-500 border-royal-blue-500 text-white"
                        : "border-gray-400 [.dark_&]:border-zinc-600 [.dark_&]:bg-zinc-800 " +
                            "text-gray-700 [.dark_&]:text-zinc-200 hover:border-indigo-400",
                    )}
                  >
                    {score}
                  </button>
                );
              })}
            </div>
            {(field.minLabel || field.maxLabel) && (
              <div className="flex justify-between mt-2 text-xs text-gray-500 [.dark_&]:text-zinc-400">
                <span>{field.minLabel}</span>
                <span>{field.maxLabel}</span>
              </div>
            )}
          </div>
        );
      }

      case "text":
      default:
        return (
          <input
            id={field.id}
            name={field.id}
            type="text"
            className={INPUT_CLASS}
            placeholder={field.placeholder}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => setValue(field.id, e.target.value)}
            disabled={isSubmitting}
            aria-required={field.required}
            maxLength={field.maxLength}
          />
        );
    }
  };

  return (
    // `flex min-h-0 flex-1` makes this a shrinkable column inside the panel, so
    // the elastic textareas below actually have a bounded height to flex within.
    // Spacing is deliberately tighter than a standalone page would use: every
    // pixel of fixed chrome raises the window height at which the form is forced
    // to scroll.
    <div className="mx-auto flex min-h-0 w-full max-w-2xl flex-1 flex-col px-6 pb-5 pt-8">
      <h2 className="text-xl font-bold text-gray-900 [.dark_&]:text-zinc-100 mb-2">
        {form.title}
      </h2>

      {form.description && (
        <p className="text-sm text-gray-600 [.dark_&]:text-zinc-400 mb-5">
          {form.description}
        </p>
      )}

      {error && (
        <div className="mb-4 px-4 py-3 rounded-md bg-red-100 [.dark_&]:bg-red-950">
          <p className="text-sm text-red-700 [.dark_&]:text-red-300">{error}</p>
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {form.fields.map((field) => (
          <div
            key={field.id}
            // Only textarea rows flex; everything else keeps its natural height
            // so the shrinking is spent where there is slack to give.
            //
            // The floor is `min-h-[88px]`, NOT `min-h-0`: 32px of label plus the
            // textarea's own 56px minimum. With min-h-0 the wrapper shrinks past
            // its own contents on short windows and the label lands on top of
            // the textarea — and because nothing overflows, the scroll container
            // never engages either. The floor is what converts "collapse" into
            // "scroll".
            className={classNames(
              "flex flex-col",
              field.type === "textarea" ? "min-h-[88px] flex-1" : "",
            )}
          >
            <label
              id={`${field.id}-label`}
              // A rating renders a `<div role="group">` of buttons, and no
              // element carries `id={field.id}` — pointing `htmlFor` at it would
              // name a target that does not exist, so clicking the label does
              // nothing and assistive tech gets a dangling reference. The
              // group's own `aria-labelledby` is the association there.
              htmlFor={field.type === "rating" ? undefined : field.id}
              className={LABEL_CLASS}
            >
              {field.label}
              {/* Marks what Submit will actually block on. Without it the only
                  notice of a required question is the error after a failed
                  attempt. The asterisk is decorative and the word carries the
                  meaning — which is also how the rating group is told apart,
                  since `group` cannot take aria-required and is named by this
                  label. */}
              {field.required && (
                <>
                  <span
                    aria-hidden="true"
                    className="text-red-600 [.dark_&]:text-red-400 ml-1"
                  >
                    *
                  </span>
                  <span className="sr-only"> (required)</span>
                </>
              )}
            </label>
            {renderField(field)}
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-3 mt-5">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            // Stays enabled mid-flight. Disabling it only guarded the button,
            // not the exit: ESC and the backdrop click leave just as freely, so
            // the guard bought nothing except a Cancel that looks broken while
            // a slow request hangs. Leaving during a submit is allowed — the
            // insert is already durable server-side either way.
            className={classNames(
              "border-[1.5px] border-gray-400 [.dark_&]:border-zinc-600 text-gray-700",
              "[.dark_&]:text-zinc-300 py-3 text-sm px-6 rounded-md font-medium",
              "hover:bg-gray-100 [.dark_&]:hover:bg-zinc-800",
            )}
          >
            Cancel
          </button>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={classNames(
            "bg-royal-blue-500 text-white py-3 text-sm px-6 rounded-md font-medium",
            isSubmitting
              ? "opacity-60 cursor-not-allowed"
              : // Kept as a literal: the original hover shade has no token, and
                // royal-blue-600 is a visibly different colour.
                "hover:bg-[#5854c7]",
          )}
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <svg
                className="inline w-4 h-4 animate-spin fill-white"
                viewBox="0 0 100 100"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="50"
                  cy="50"
                  r="35"
                  stroke="currentColor"
                  strokeWidth="10"
                  fill="none"
                  strokeDasharray="164.93 54.98"
                />
              </svg>
              Submitting...
            </span>
          ) : (
            submitLabel || "Submit"
          )}
        </button>
      </div>
    </div>
  );
};

export default FeedbackFields;
